import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import SleeveModal from './SleeveModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { Sleeves } from "../../../../types/Sleeves";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Sleeve: React.FC = () => {
  const [sleeves, setSleeves] = useState<Sleeves[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Sleeves | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchSleeve = async () => {
    try {
      const data = await apiRequest<Sleeves[]>({
        method: 'get',
        url: 'sleeves',
      });
      setSleeves(data);
    } catch (error) {
      showError('Failed to fetch sleeve');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSleeve();
  }, []);

  const handleEditClick = (record: Sleeves) => {
    setIsEditMode(true);
    setInitialValues({
      sleeve_id: record.sleeve_id,
      sleeve_name: record.sleeve_name,
      sleeve_mesh_name: record.sleeve_mesh_name,
      sleeve_status: record.sleeve_status,
      sleeve_image: record.sleeve_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchSleeve();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (sleeve_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-sleeve/${sleeve_id}`,
      });
      showSuccess('Sleeve deleted');
      fetchSleeve();
    } catch {
      showError('Failed to delete sleeve');
    }
  };

  const handleStatusToggle = async (sleeveId: number) => {
    setSleeves((prev) =>
      prev.map((item) =>
        item.sleeve_id === sleeveId
          ? { ...item, sleeve_status: item.sleeve_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-sleeve/${sleeveId}`,
      });
      showSuccess('Sleeve status updated');
    } catch (error) {
      showError('Failed to update status');
      setSleeves((prev) =>
        prev.map((item) =>
          item.sleeve_id === sleeveId
            ? { ...item, sleeve_status: item.sleeve_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Sleeves>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Sleeves> = [
    
    {
      title: 'Sleeve',
      dataIndex: 'sleeve_name',
      key: 'sleeve_name',
      width: 500,
      filters: Array.from(new Set(sleeves.map((s) => s.sleeve_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.sleeve_name || null,
      onFilter: (value, record) => record.sleeve_name.includes(value as string),
      sorter: (a, b) => a.sleeve_name.localeCompare(b.sleeve_name),
      sortOrder: sortedInfo.columnKey === 'sleeve_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.sleeve_name}</div>,
    },
    {
      title: 'Sleeve Mesh Name',
      dataIndex: 'sleeve_mesh_name',
      key: 'sleeve_mesh_name',
      width: 500,
      filters: Array.from(new Set(sleeves.map((s) => s.sleeve_mesh_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.sleeve_mesh_name || null,
      onFilter: (value, record) => record.sleeve_mesh_name.includes(value as string),
      sorter: (a, b) => a.sleeve_mesh_name.localeCompare(b.sleeve_mesh_name),
      sortOrder: sortedInfo.columnKey === 'sleeve_mesh_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.sleeve_mesh_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'sleeve_image',
      key: 'sleeve_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.sleeve_image}
            alt="Sleeve"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Sleeves) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.sleeve_status}
                onChange={() => handleStatusToggle(record.sleeve_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this sleeve?"
              onConfirm={() => handleDelete(record.sleeve_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Sleeve</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Sleeve 
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Sleeves>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="sleeve_id"
          dataSource={sleeves.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Sleeve found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, sleeves.length)} of{' '}
                {sleeves.length}
              </span>
              <Pagination
                {...pagination}
                total={sleeves.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <SleeveModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        sleeves={sleeves}
      />
    </>
  );
};

export default Sleeve;